import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Calendar } from "./ui/calendar";
import { toast } from "sonner";
import { CheckCircle2, Upload, X, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { TIME_WINDOWS } from "../data";
import { useContent } from "../ContentContext";
import { submitQuote, submitBooking, getAvailability } from "../api";

// Exact copy — do not tweak without product approval
const SUCCESS_MSG = "Thank you! Your estimate request has been received. Our team will contact you shortly.";
const SCHEDULE_SUCCESS_MSG = "Thank you! Your appointment request has been received. Our team will contact you shortly to confirm.";
const ERROR_MSG = "We could not submit your request at this time. Please try again or call us directly.";

// Turn FastAPI 422 validation `detail` (array of {loc, msg, type}) into a friendly one-liner.
// Falls back to the exact ERROR_MSG copy for anything unknown.
function friendlyError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : null;
    if (field) return `Please double-check the "${String(field).replace(/_/g, " ")}" field and try again.`;
  }
  return ERROR_MSG;
}

const fmt = (d) => {
  if (!d) return "";
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const field = "mt-1.5 bg-white border-black/10 focus-visible:ring-[var(--brass)] focus-visible:ring-2";

export default function QuoteDialog({ open, onOpenChange, mode }) {
  const [tab, setTab] = useState("estimate");
  useEffect(() => { if (open) setTab(mode || "estimate"); }, [open, mode]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quote-dialog"
        className="max-w-2xl max-h-[92vh] overflow-y-auto p-0"
        style={{ background: "var(--bone)" }}>
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="font-display font-extrabold uppercase tracking-tight text-2xl" style={{ color: "var(--pine)" }}>
            Get in touch
          </DialogTitle>
          <DialogDescription className="sr-only">Request a free estimate or schedule a visit with Top of the Line Tree Services LLC.</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="px-6 pb-6">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="estimate" data-testid="tab-estimate">Free Estimate</TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule Visit</TabsTrigger>
          </TabsList>
          <TabsContent value="estimate"><EstimateForm onDone={() => onOpenChange(false)} /></TabsContent>
          <TabsContent value="schedule"><ScheduleForm onDone={() => onOpenChange(false)} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function Success({ text }) {
  const { company } = useContent();
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
      className="py-10 text-center" data-testid="success-message">
      <CheckCircle2 size={56} className="mx-auto" style={{ color: "var(--brass)" }} />
      <p className="mt-5 text-base leading-relaxed max-w-md mx-auto" style={{ color: "var(--pine)" }}>{text}</p>
      <a href={`tel:${company.phoneRaw}`} className="mt-4 inline-block font-semibold" style={{ color: "var(--brass)" }}>
        Or call us now: {company.phone}
      </a>
    </motion.div>
  );
}

function EstimateForm({ onDone }) {
  const { services } = useContent();
  const serviceNames = services.map((s) => s.title);
  const empty = {
    full_name: "", phone: "", email: "", address: "", city: "", state: "AL", zip_code: "",
    property_type: "residential", service: "", description: "", preferred_date: "",
    preferred_time: "", contact_method: "call", is_emergency: false, confirmed: false, website: "",
  };
  const [f, setF] = useState(empty);
  const [date, setDate] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submittingRef = useRef(false); // guards against rapid double-submits
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    const arr = await Promise.all(files.map((file) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file);
    })));
    setPhotos((p) => [...p, ...arr].slice(0, 5));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;              // extra double-click guard
    if (!f.confirmed) { toast.error("Please confirm your information is correct."); return; }
    if (!f.service) { toast.error("Please select a service."); return; }
    submittingRef.current = true;
    setLoading(true);
    try {
      await submitQuote({ ...f, preferred_date: fmt(date), photos });
      // clear the form BEFORE flipping to the success screen
      setF(empty); setDate(null); setPhotos([]);
      setDone(true);
      toast.success(SUCCESS_MSG);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setLoading(false); submittingRef.current = false; }
  };

  if (done) return <Success text={SUCCESS_MSG} />;

  return (
    <form onSubmit={submit} className="space-y-4 pt-4" data-testid="estimate-form">
      <input type="text" name="website" value={f.website} onChange={(e) => set("website", e.target.value)}
             className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Full name *</Label><Input required data-testid="q-name" className={field} value={f.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
        <div><Label>Phone number *</Label><Input required data-testid="q-phone" className={field} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
      </div>
      <div><Label>Email *</Label><Input required type="email" data-testid="q-email" className={field} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
      <div><Label>Street address *</Label><Input required data-testid="q-address" className={field} value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
      <div className="grid grid-cols-3 gap-4">
        <div><Label>City *</Label><Input required data-testid="q-city" className={field} value={f.city} onChange={(e) => set("city", e.target.value)} /></div>
        <div><Label>State *</Label><Input required data-testid="q-state" className={field} value={f.state} onChange={(e) => set("state", e.target.value)} /></div>
        <div><Label>ZIP *</Label><Input required data-testid="q-zip" className={field} value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} /></div>
      </div>

      <div>
        <Label>Property type *</Label>
        <RadioGroup value={f.property_type} onValueChange={(v) => set("property_type", v)} className="flex gap-6 mt-2">
          <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="residential" data-testid="q-residential" /> Residential</label>
          <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="commercial" data-testid="q-commercial" /> Commercial</label>
        </RadioGroup>
      </div>

      <div>
        <Label>Service needed *</Label>
        <Select value={f.service} onValueChange={(v) => set("service", v)}>
          <SelectTrigger className={field} data-testid="q-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
          <SelectContent>{serviceNames.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div><Label>Describe the work *</Label><Textarea required data-testid="q-description" className={field} rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} /></div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Preferred date</Label>
          <div className="mt-1.5 rounded-lg border bg-white p-2 flex justify-center" style={{ borderColor: "rgba(0,0,0,0.1)" }}>
            <Calendar mode="single" selected={date} onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0}
                      data-testid="q-calendar" />
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Preferred time</Label>
            <Select value={f.preferred_time} onValueChange={(v) => set("preferred_time", v)}>
              <SelectTrigger className={field} data-testid="q-time"><SelectValue placeholder="Any time" /></SelectTrigger>
              <SelectContent>{TIME_WINDOWS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Preferred contact</Label>
            <Select value={f.contact_method} onValueChange={(v) => set("contact_method", v)}>
              <SelectTrigger className={field} data-testid="q-contact"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="call">Phone call</SelectItem>
                <SelectItem value="text">Text message</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Is this an emergency?</Label>
            <RadioGroup value={f.is_emergency ? "yes" : "no"} onValueChange={(v) => set("is_emergency", v === "yes")} className="flex gap-6 mt-2">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="yes" data-testid="q-emergency-yes" /> Yes</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="no" data-testid="q-emergency-no" /> No</label>
            </RadioGroup>
          </div>
        </div>
      </div>

      <div>
        <Label>Photos or videos (optional)</Label>
        <label data-testid="q-upload" className="mt-1.5 flex items-center justify-center gap-2 border border-dashed rounded-lg py-6 cursor-pointer hover:bg-black/5 transition-colors"
               style={{ borderColor: "var(--brass)" }}>
          <Upload size={18} style={{ color: "var(--brass)" }} />
          <span className="text-sm" style={{ color: "var(--pine)" }}>Tap to upload (up to 5)</span>
          <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={onFiles} />
        </label>
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {photos.map((p, i) => (
              <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border" style={{ borderColor: "rgba(0,0,0,0.1)" }}>
                <img src={p} alt="upload" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                        className="absolute top-0 right-0 bg-black/60 text-white p-0.5"><X size={12} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <label className="flex items-start gap-3 cursor-pointer pt-2">
        <Checkbox checked={f.confirmed} onCheckedChange={(v) => set("confirmed", !!v)} data-testid="q-confirm" className="mt-1" />
        <span className="text-sm" style={{ color: "#4a4a4a" }}>I confirm the information above is correct.</span>
      </label>

      <button type="submit" disabled={loading} data-testid="q-submit"
              className="w-full rounded-full py-4 font-semibold flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] disabled:opacity-60"
              style={{ background: "var(--sun)", color: "var(--pine)" }}>
        {loading && <Loader2 size={18} className="animate-spin" />} Submit Estimate Request
      </button>
      <p className="text-xs text-center" style={{ color: "#888" }}>
        Protected against spam. A request is not a confirmed appointment until we contact you.
      </p>
    </form>
  );
}

function ScheduleForm({ onDone }) {
  const { services } = useContent();
  const serviceNames = services.map((s) => s.title);
  const empty = { full_name: "", phone: "", email: "", service: "", time_window: TIME_WINDOWS[0], notes: "", website: "" };
  const [f, setF] = useState(empty);
  const [date, setDate] = useState(null);
  const [avail, setAvail] = useState({ taken: [], blocked: [] });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submittingRef = useRef(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { getAvailability().then(setAvail).catch(() => {}); }, []);
  const disabled = (d) => {
    const s = fmt(d);
    return d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0 ||
      avail.taken.includes(s) || avail.blocked.includes(s);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!date) { toast.error("Please select a date."); return; }
    submittingRef.current = true;
    setLoading(true);
    try {
      await submitBooking({ ...f, date: fmt(date) });
      setF(empty); setDate(null);
      setDone(true);
      toast.success(SCHEDULE_SUCCESS_MSG);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setLoading(false); submittingRef.current = false; }
  };

  if (done) return <Success text={SCHEDULE_SUCCESS_MSG} />;

  return (
    <form onSubmit={submit} className="space-y-4 pt-4" data-testid="schedule-form">
      <input type="text" name="website" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Full name *</Label><Input required data-testid="s-name" className={field} value={f.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
        <div><Label>Phone *</Label><Input required data-testid="s-phone" className={field} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
      </div>
      <div><Label>Email *</Label><Input required type="email" data-testid="s-email" className={field} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
      <div><Label>Service</Label>
        <Select value={f.service} onValueChange={(v) => set("service", v)}>
          <SelectTrigger className={field} data-testid="s-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
          <SelectContent>{serviceNames.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Choose an available date *</Label>
          <div className="mt-1.5 rounded-lg border bg-white p-2 flex justify-center" style={{ borderColor: "rgba(0,0,0,0.1)" }}>
            <Calendar mode="single" selected={date} onSelect={setDate} disabled={disabled} data-testid="s-calendar" />
          </div>
        </div>
        <div>
          <Label>Preferred time window</Label>
          <Select value={f.time_window} onValueChange={(v) => set("time_window", v)}>
            <SelectTrigger className={field} data-testid="s-time"><SelectValue /></SelectTrigger>
            <SelectContent>{TIME_WINDOWS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Label className="mt-4 block">Notes</Label>
          <Textarea data-testid="s-notes" className={field} rows={4} value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>
      </div>
      <p className="text-xs" style={{ color: "#888" }}>Sundays and taken dates are unavailable. Selecting a date does not guarantee an appointment.</p>
      <button type="submit" disabled={loading} data-testid="s-submit"
              className="w-full rounded-full py-4 font-semibold flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] disabled:opacity-60"
              style={{ background: "var(--sun)", color: "var(--pine)" }}>
        {loading && <Loader2 size={18} className="animate-spin" />} Request This Date
      </button>
    </form>
  );
}
